import { ToolPointer } from './tool';
import { PlaneToolBase } from './planeToolBase';
import { add, dist, mul, sub, Vec3 } from '../core/vec';
import { formatLength, parseLength } from '../core/units';

const CIRCLE_SEGMENTS = 24;

/** Kreis-Werkzeug (C): Mittelpunkt + Radius, 24 Segmente wie SketchUp-Standard. */
export class CircleTool extends PlaneToolBase {
  readonly id = 'circle';
  readonly label = 'Kreis';
  readonly shortcut = 'C';
  readonly cursor = 'crosshair';
  readonly hint = 'Mittelpunkt setzen, Radius wählen · Zahl tippen = Radius · Auf einer Fläche zeichnen teilt diese';

  private center: Vec3 | null = null;
  private current: Vec3 | null = null;
  private previewRadius: number | null = null;

  enter(): void {
    this.cancel();
  }

  exit(): void {
    this.ctx.preview.clear();
    this.center = null;
  }

  pointerMove(e: ToolPointer): void {
    this.ctx.preview.clear();
    const snap = this.getSnap(e);
    this.showSnapDot(snap);
    if (!this.center) {
      this.current = snap.point;
      this.ctx.requestUpdate();
      return;
    }
    this.pickPlane(this.center, e);
    this.current = this.snapInPlane(e, snap, [this.center]);
    this.showInference();
    this.drawPreview();
    this.ctx.ui.setStatus(`Radius: ${formatLength(dist(this.center, this.current), this.ctx.units())} · Zahl tippen = Radius`);
    this.ctx.requestUpdate();
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    this.pointerMove(e); // Frischen Snap für die aktuelle Position holen
    if (!this.center) {
      const snap = this.snap!;
      this.center = snap.point;
      this.adoptPlane(snap);
      this.pressAt = { x: e.x, y: e.y };
      this.ctx.ui.setStatus('Radius auswählen · Zahl tippen = Radius');
    } else {
      this.commit(this.radius());
    }
    this.ctx.requestUpdate();
  }

  /** Ziehen und Loslassen setzt den Radius (alternativ zum zweiten Klick). */
  pointerUp(e: ToolPointer): void {
    const press = this.pressAt;
    this.pressAt = null;
    if (!press || !this.center || Math.hypot(e.x - press.x, e.y - press.y) < 6) return;
    this.pointerMove(e);
    this.commit(this.radius());
  }

  private pressAt: { x: number; y: number } | null = null;

  private radius(): number {
    if (this.previewRadius !== null) return this.previewRadius;
    return this.center && this.current ? dist(this.center, this.current) : 0;
  }

  private drawPreview(): void {
    if (!this.center || !this.current) return;
    const r = this.radius();
    if (r < 1e-6) return;
    this.ctx.preview.polyline(this.circlePoints(this.center, r), 0x333333, true);
    this.ctx.preview.line(this.center, this.current, 0x666666);
  }

  private circlePoints(center: Vec3, r: number): Vec3[] {
    const f = this.frame();
    const pts: Vec3[] = [];
    for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
      const a = (i / CIRCLE_SEGMENTS) * Math.PI * 2;
      pts.push(add(center, add(mul(f.u, Math.cos(a) * r), mul(f.v, Math.sin(a) * r))));
    }
    return pts;
  }

  private commit(r: number): void {
    if (!this.center || r < 1e-6) return;
    this.commitShape(this.circlePoints(this.center, r));
    this.center = null;
    this.current = null;
    this.previewRadius = null;
    this.resetPlane();
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }

  wantsVcb(): boolean {
    return this.center !== null;
  }

  vcbDisplay(): string | null {
    if (!this.center) return null;
    return formatLength(this.radius(), this.ctx.units());
  }

  applyVcb(raw: string): void {
    if (!this.center) return;
    const r = parseLength(raw, this.ctx.units());
    if (r === null || r <= 0) return;
    this.commit(r);
  }

  cancel(): void {
    this.center = null;
    this.current = null;
    this.previewRadius = null;
    this.resetPlane();
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }
}

void sub;
